import {
  getObject,
  getOptionalObject,
  getList,
} from "@surya-digital/leo-ts-types";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { Code, CodeDTO } from "../types/code";
import { SampleBreakdown, SampleBreakdownDTO } from "./sampleBreakdown";
import { SingleSelectResultV1, SingleSelectResultV1DTO } from "./singleSelectResultV1";
import { SingleSelectGraphTypeV1 } from "./singleSelectGraphTypeV1";
import { ProportionChartType } from "./proportionChartType";
import { SingleSelectWithProportionGraphTypeV1 } from "./singleSelectWithProportionGraphTypeV1";
import { SingleSelectNpsResultV1, SingleSelectNpsResultV1DTO } from "./singleSelectNpsResultV1";
import { SingleSelectNpsGraphTypeV1 } from "./singleSelectNpsGraphTypeV1";
import { MultiSelectResultV1, MultiSelectResultV1DTO } from "./multiSelectResultV1";
import { MultiSelectGraphTypeV1 } from "./multiSelectGraphTypeV1";
import { RankingResultV1, RankingResultV1DTO } from "./rankingResultV1";
import { RankingGraphTypeV1 } from "./rankingGraphTypeV1";
import { OpenEndedFieldResultV1, OpenEndedFieldResultV1DTO } from "./openEndedFieldResultV1";
import { OpenEndedGraphTypeV1 } from "./openEndedGraphTypeV1";
import { GridResultV1, GridResultV1DTO, GridResultV1Enums } from "./gridResultV1";


export namespace GroupElementResultV1Enums {
  export namespace QuestionViewType {
    export interface SingleSelectDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      singleSelectResults: SingleSelectResultV1DTO[];
      singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1DTO;
    }
    
    export class SingleSelect {
      readonly isQuestionViewTypeSingleSelect: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly singleSelectResults: SingleSelectResultV1[];
      readonly singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1;
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      singleSelectResults: SingleSelectResultV1[], 
      singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.singleSelectResults = singleSelectResults;
        this.singleSelectGraphType = singleSelectGraphType;
      }
    
      static fromDTO(dto: SingleSelectDTO): SingleSelect {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const singleSelectResults = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
          dto,
          "singleSelectResults",
          SingleSelectResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectResultsError
        );
        const singleSelectGraphType = SingleSelectGraphTypeV1.fromDTO(getObject(dto, "singleSelectGraphType", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectGraphTypeError));
        return new SingleSelect(
          sampleBreakdown,
          singleSelectResults,
          singleSelectGraphType
        );
      }
    
      toDTO(): SingleSelectDTO {
        const dto: SingleSelectDTO = {
          case: "SINGLE_SELECT",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            singleSelectResults: this.singleSelectResults.map((e: SingleSelectResultV1) => { return e.toDTO() }),
            singleSelectGraphType: SingleSelectGraphTypeV1.toDTO(this.singleSelectGraphType)
        };
        return dto;
      }
      }
    
    SingleSelect.prototype.toString = function toString(): string {
      return `QuestionViewType.SINGLE_SELECT(sampleBreakdown='${this.sampleBreakdown}',singleSelectResults='${this.singleSelectResults}',singleSelectGraphType='${this.singleSelectGraphType}')`;
    };
    
    export interface SingleSelectWithProportionDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      singleSelectResults: SingleSelectResultV1DTO[];
      proportionChartType: ProportionChartType.ProportionChartTypeDTO;
      singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1DTO;
      naOptionCode?: CodeDTO | null;
    }
    
    export class SingleSelectWithProportion {
      readonly isQuestionViewTypeSingleSelectWithProportion: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly singleSelectResults: SingleSelectResultV1[];
      readonly proportionChartType: ProportionChartType.ProportionChartType;
      readonly singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1;
      readonly naOptionCode: Code | null;
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      singleSelectResults: SingleSelectResultV1[], 
      proportionChartType: ProportionChartType.ProportionChartType, 
      singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1, 
      naOptionCode: Code | null = null
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.singleSelectResults = singleSelectResults;
        this.proportionChartType = proportionChartType;
        this.singleSelectWithProportionGraphType = singleSelectWithProportionGraphType;
        this.naOptionCode = naOptionCode;
      }
    
      static fromDTO(dto: SingleSelectWithProportionDTO): SingleSelectWithProportion {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const singleSelectResults = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
          dto,
          "singleSelectResults",
          SingleSelectResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectResultsError
        );
        const proportionChartType = ProportionChartType.fromDTO(getObject(dto, "proportionChartType", InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionProportionChartTypeError));
        const singleSelectWithProportionGraphType = SingleSelectWithProportionGraphTypeV1.fromDTO(getObject(dto, "singleSelectWithProportionGraphType", InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError));
        let naOptionCode = getOptionalObject(dto, "naOptionCode");
        if(naOptionCode !== undefined && naOptionCode !== null) {
          naOptionCode = Code.fromDTO(getObject(dto, "naOptionCode", InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionNaOptionCodeError));
        }
        return new SingleSelectWithProportion(
          sampleBreakdown,
          singleSelectResults,
          proportionChartType,
          singleSelectWithProportionGraphType,
          naOptionCode
        );
      }
    
      toDTO(): SingleSelectWithProportionDTO {
        const dto: SingleSelectWithProportionDTO = {
          case: "SINGLE_SELECT_WITH_PROPORTION",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            singleSelectResults: this.singleSelectResults.map((e: SingleSelectResultV1) => { return e.toDTO() }),
            proportionChartType: this.proportionChartType.toDTO(),
            singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.toDTO(this.singleSelectWithProportionGraphType),
        };
        if (this.naOptionCode !== undefined && this.naOptionCode !== null){
          dto.naOptionCode = this.naOptionCode.toDTO();
        }
        return dto;
      }
      }
    
    SingleSelectWithProportion.prototype.toString = function toString(): string {
      return `QuestionViewType.SINGLE_SELECT_WITH_PROPORTION(sampleBreakdown='${this.sampleBreakdown}',singleSelectResults='${this.singleSelectResults}',proportionChartType='${this.proportionChartType}',singleSelectWithProportionGraphType='${this.singleSelectWithProportionGraphType}',naOptionCode='${this.naOptionCode}')`;
    };
    
    export interface SingleSelectNpsDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      promoters: SingleSelectNpsResultV1DTO;
      passives: SingleSelectNpsResultV1DTO;
      detractors: SingleSelectNpsResultV1DTO;
      singleSelectNpsResultsBreakdownV1: SingleSelectResultV1DTO[];
      singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1DTO;
    }
    
    export class SingleSelectNps {
      readonly isQuestionViewTypeSingleSelectNps: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly promoters: SingleSelectNpsResultV1;
      readonly passives: SingleSelectNpsResultV1;
      readonly detractors: SingleSelectNpsResultV1;
      readonly singleSelectNpsResultsBreakdownV1: SingleSelectResultV1[];
      readonly singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1;
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      promoters: SingleSelectNpsResultV1, 
      passives: SingleSelectNpsResultV1, 
      detractors: SingleSelectNpsResultV1, 
      singleSelectNpsResultsBreakdownV1: SingleSelectResultV1[], 
      singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.promoters = promoters;
        this.passives = passives;
        this.detractors = detractors;
        this.singleSelectNpsResultsBreakdownV1 = singleSelectNpsResultsBreakdownV1;
        this.singleSelectNpsGraphType = singleSelectNpsGraphType;
      }
    
      static fromDTO(dto: SingleSelectNpsDTO): SingleSelectNps {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const promoters = SingleSelectNpsResultV1.fromDTO(getObject(dto, "promoters", InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPromotersError));
        const passives = SingleSelectNpsResultV1.fromDTO(getObject(dto, "passives", InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPassivesError));
        const detractors = SingleSelectNpsResultV1.fromDTO(getObject(dto, "detractors", InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsDetractorsError));
        const singleSelectNpsResultsBreakdownV1 = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
          dto,
          "singleSelectNpsResultsBreakdownV1",
          SingleSelectResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsResultsBreakdownV1Error
        );
        const singleSelectNpsGraphType = SingleSelectNpsGraphTypeV1.fromDTO(getObject(dto, "singleSelectNpsGraphType", InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsGraphTypeError));
        return new SingleSelectNps(
          sampleBreakdown,
          promoters,
          passives,
          detractors,
          singleSelectNpsResultsBreakdownV1,
          singleSelectNpsGraphType
        );
      }
    
      toDTO(): SingleSelectNpsDTO {
        const dto: SingleSelectNpsDTO = {
          case: "SINGLE_SELECT_NPS",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            promoters: this.promoters.toDTO(),
            passives: this.passives.toDTO(),
            detractors: this.detractors.toDTO(),
            singleSelectNpsResultsBreakdownV1: this.singleSelectNpsResultsBreakdownV1.map((e: SingleSelectResultV1) => { return e.toDTO() }),
            singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.toDTO(this.singleSelectNpsGraphType)
        };
        return dto;
      }
      }
    
    SingleSelectNps.prototype.toString = function toString(): string {
      return `QuestionViewType.SINGLE_SELECT_NPS(sampleBreakdown='${this.sampleBreakdown}',promoters='${this.promoters}',passives='${this.passives}',detractors='${this.detractors}',singleSelectNpsResultsBreakdownV1='${this.singleSelectNpsResultsBreakdownV1}',singleSelectNpsGraphType='${this.singleSelectNpsGraphType}')`;
    };
    
    export interface MultiSelectDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      multiSelectResults: MultiSelectResultV1DTO[];
      multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1DTO;
    }
    
    export class MultiSelect {
      readonly isQuestionViewTypeMultiSelect: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly multiSelectResults: MultiSelectResultV1[];
      readonly multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1;
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      multiSelectResults: MultiSelectResultV1[], 
      multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.multiSelectResults = multiSelectResults;
        this.multiSelectGraphType = multiSelectGraphType;
      }
    
      static fromDTO(dto: MultiSelectDTO): MultiSelect {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const multiSelectResults = getList<MultiSelectResultV1DTO, MultiSelectResultV1>(
          dto,
          "multiSelectResults",
          MultiSelectResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectResultsError
        );
        const multiSelectGraphType = MultiSelectGraphTypeV1.fromDTO(getObject(dto, "multiSelectGraphType", InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectGraphTypeError));
        return new MultiSelect(
          sampleBreakdown,
          multiSelectResults,
          multiSelectGraphType
        );
      }
    
      toDTO(): MultiSelectDTO {
        const dto: MultiSelectDTO = {
          case: "MULTI_SELECT",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            multiSelectResults: this.multiSelectResults.map((e: MultiSelectResultV1) => { return e.toDTO() }),
            multiSelectGraphType: MultiSelectGraphTypeV1.toDTO(this.multiSelectGraphType)
        };
        return dto;
      }
      }
    
    MultiSelect.prototype.toString = function toString(): string {
      return `QuestionViewType.MULTI_SELECT(sampleBreakdown='${this.sampleBreakdown}',multiSelectResults='${this.multiSelectResults}',multiSelectGraphType='${this.multiSelectGraphType}')`;
    };
    
    export interface RankingDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      rankingResults: RankingResultV1DTO[];
      rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1DTO;
    }
    
    export class Ranking {
      readonly isQuestionViewTypeRanking: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly rankingResults: RankingResultV1[];
      readonly rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1;
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      rankingResults: RankingResultV1[], 
      rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.rankingResults = rankingResults;
        this.rankingGraphType = rankingGraphType;
      }
    
      static fromDTO(dto: RankingDTO): Ranking {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const rankingResults = getList<RankingResultV1DTO, RankingResultV1>(
          dto,
          "rankingResults",
          RankingResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeRankingRankingResultsError
        );
        const rankingGraphType = RankingGraphTypeV1.fromDTO(getObject(dto, "rankingGraphType", InvalidGroupElementResultV1QuestionViewTypeRankingRankingGraphTypeError));
        return new Ranking(
          sampleBreakdown,
          rankingResults,
          rankingGraphType
        );
      }
    
      toDTO(): RankingDTO {
        const dto: RankingDTO = {
          case: "RANKING",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            rankingResults: this.rankingResults.map((e: RankingResultV1) => { return e.toDTO() }),
            rankingGraphType: RankingGraphTypeV1.toDTO(this.rankingGraphType)
        };
        return dto;
      }
      }
    
    Ranking.prototype.toString = function toString(): string {
      return `QuestionViewType.RANKING(sampleBreakdown='${this.sampleBreakdown}',rankingResults='${this.rankingResults}',rankingGraphType='${this.rankingGraphType}')`;
    };
    
    export interface OpenEndedDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      openEndedResults: OpenEndedFieldResultV1DTO[];
      openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1DTO;
    }
    
    export class OpenEnded {
      readonly isQuestionViewTypeOpenEnded: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly openEndedResults: OpenEndedFieldResultV1[];
      readonly openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1;
    
      static readonly constraints = {
        "openEndedResults": {
          "maxSize": 16,
        },
      }
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      openEndedResults: OpenEndedFieldResultV1[], 
      openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1
      ) {
        this.sampleBreakdown = sampleBreakdown;
        OpenEnded.validateOpenEndedResults(openEndedResults);
        this.openEndedResults = openEndedResults;
        this.openEndedGraphType = openEndedGraphType;
      }
    
      static fromDTO(dto: OpenEndedDTO): OpenEnded {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const openEndedResults = getList<OpenEndedFieldResultV1DTO, OpenEndedFieldResultV1>(
          dto,
          "openEndedResults",
          OpenEndedFieldResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsError
        );
        const openEndedGraphType = OpenEndedGraphTypeV1.fromDTO(getObject(dto, "openEndedGraphType", InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedGraphTypeError));
        return new OpenEnded(
          sampleBreakdown,
          openEndedResults,
          openEndedGraphType
        );
      }
    
      toDTO(): OpenEndedDTO {
        const dto: OpenEndedDTO = {
          case: "OPEN_ENDED",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            openEndedResults: this.openEndedResults.map((e: OpenEndedFieldResultV1) => { return e.toDTO() }),
            openEndedGraphType: OpenEndedGraphTypeV1.toDTO(this.openEndedGraphType)
        };
        return dto;
      }
      private static validateOpenEndedResults = (openEndedResults: OpenEndedFieldResultV1[]): void => {
        if(openEndedResults.length > 16) {
          throw new InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsMaxSizeError(
            16,
            `Attribute openEndedResults is too long. Size is ${openEndedResults.length}. Max size is 16.`
          );
        }
      }
      
    }
    
    OpenEnded.prototype.toString = function toString(): string {
      return `QuestionViewType.OPEN_ENDED(sampleBreakdown='${this.sampleBreakdown}',openEndedResults='${this.openEndedResults}',openEndedGraphType='${this.openEndedGraphType}')`;
    };
    
    export interface GridDTO {
      case: string;
      sampleBreakdown: SampleBreakdownDTO;
      gridResults: GridResultV1DTO[];
    }
    
    export class Grid {
      readonly isQuestionViewTypeGrid: boolean = true;
    
      readonly sampleBreakdown: SampleBreakdown;
      readonly gridResults: GridResultV1[];
    
    
      constructor(
      sampleBreakdown: SampleBreakdown, 
      gridResults: GridResultV1[]
      ) {
        this.sampleBreakdown = sampleBreakdown;
        this.gridResults = gridResults;
      }
    
      static fromDTO(dto: GridDTO): Grid {
        const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError));
        const gridResults = getList<GridResultV1DTO, GridResultV1>(
          dto,
          "gridResults",
          GridResultV1.fromDTO,
          InvalidGroupElementResultV1QuestionViewTypeGridGridResultsError
        );
        return new Grid(
          sampleBreakdown,
          gridResults
        );
      }
    
      toDTO(): GridDTO {
        const dto: GridDTO = {
          case: "GRID",
            sampleBreakdown: this.sampleBreakdown.toDTO(),
            gridResults: this.gridResults.map((e: GridResultV1) => { return e.toDTO() })
        };
        return dto;
      }
      }
    
    Grid.prototype.toString = function toString(): string {
      return `QuestionViewType.GRID(sampleBreakdown='${this.sampleBreakdown}',gridResults='${this.gridResults}')`;
    };
    
  
    export type QuestionViewType = SingleSelect | SingleSelectWithProportion | SingleSelectNps | MultiSelect | Ranking | OpenEnded | Grid;
  
    export type QuestionViewTypeDTO = SingleSelectDTO | SingleSelectWithProportionDTO | SingleSelectNpsDTO | MultiSelectDTO | RankingDTO | OpenEndedDTO | GridDTO;
  
    export const fromDTO = (dto: QuestionViewTypeDTO): QuestionViewType => {
      switch (dto.case) {
        case "SINGLE_SELECT":
            return SingleSelect.fromDTO(dto as SingleSelectDTO);
        case "SINGLE_SELECT_WITH_PROPORTION":
            return SingleSelectWithProportion.fromDTO(dto as SingleSelectWithProportionDTO);
        case "SINGLE_SELECT_NPS":
            return SingleSelectNps.fromDTO(dto as SingleSelectNpsDTO);
        case "MULTI_SELECT":
            return MultiSelect.fromDTO(dto as MultiSelectDTO);
        case "RANKING":
            return Ranking.fromDTO(dto as RankingDTO);
        case "OPEN_ENDED":
            return OpenEnded.fromDTO(dto as OpenEndedDTO);
        case "GRID":
            return Grid.fromDTO(dto as GridDTO);
        default:
          throw new InvalidGroupElementResultV1QuestionViewTypeError(
              `Case ${dto.case} is not valid case of QuestionViewType.`
          );
      }
    };
  }
  
}
export interface GroupElementResultV1DTO {
  question?: SurveyLocalizedTextDTO | null;
  questionCode: CodeDTO;
  questionViewType: GroupElementResultV1Enums.QuestionViewType.QuestionViewTypeDTO;
}

export class GroupElementResultV1 {
  readonly isGroupElementResultV1GroupElementResultV1: boolean = true;

  readonly question: SurveyLocalizedText | null;
  readonly questionCode: Code;
  readonly questionViewType: GroupElementResultV1Enums.QuestionViewType.QuestionViewType;


  constructor(
  question: SurveyLocalizedText | null = null, 
  questionCode: Code, 
  questionViewType: GroupElementResultV1Enums.QuestionViewType.QuestionViewType
  ) {
    this.question = question;
    this.questionCode = questionCode;
    this.questionViewType = questionViewType;
  }

  static fromDTO(dto: GroupElementResultV1DTO): GroupElementResultV1 {
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidGroupElementResultV1QuestionError));
    }
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidGroupElementResultV1QuestionCodeError));
    const questionViewType = GroupElementResultV1Enums.QuestionViewType.fromDTO(getObject(dto, "questionViewType", InvalidGroupElementResultV1QuestionViewTypeError));
    return new GroupElementResultV1(
      question,
      questionCode,
      questionViewType
    );
  }

  toDTO(): GroupElementResultV1DTO {
    const dto: GroupElementResultV1DTO = {
        questionCode: this.questionCode.toDTO(),
        questionViewType: this.questionViewType.toDTO()
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    return dto;
  }
  }

GroupElementResultV1.prototype.toString = function toString(): string {
  return `GroupElementResultV1(question='${this.question}',questionCode='${this.questionCode}',questionViewType='${this.questionViewType}')`;
};

export class InvalidGroupElementResultV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1Error";
  }
}
export class InvalidGroupElementResultV1QuestionError extends InvalidGroupElementResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionError";
  }
}

export class InvalidGroupElementResultV1QuestionCodeError extends InvalidGroupElementResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionCodeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeError extends InvalidGroupElementResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectResultsError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectResultsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectSingleSelectGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSingleSelectResultsError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSingleSelectResultsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionProportionChartTypeError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionProportionChartTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionNaOptionCodeError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectWithProportionNaOptionCodeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPromotersError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPromotersError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPassivesError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsPassivesError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsDetractorsError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsDetractorsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsResultsBreakdownV1Error extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsResultsBreakdownV1Error";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeSingleSelectNpsSingleSelectNpsGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeMultiSelectError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeMultiSelectError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeMultiSelectSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeMultiSelectSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectResultsError extends InvalidGroupElementResultV1QuestionViewTypeMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectResultsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeMultiSelectMultiSelectGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeRankingError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeRankingError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeRankingSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeRankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeRankingSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeRankingRankingResultsError extends InvalidGroupElementResultV1QuestionViewTypeRankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeRankingRankingResultsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeRankingRankingGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeRankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeRankingRankingGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeOpenEndedError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeOpenEndedError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeOpenEndedSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeOpenEndedSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsError extends InvalidGroupElementResultV1QuestionViewTypeOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsMaxSizeError extends InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedResultsMaxSizeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedGraphTypeError extends InvalidGroupElementResultV1QuestionViewTypeOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeOpenEndedOpenEndedGraphTypeError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeGridError extends InvalidGroupElementResultV1QuestionViewTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeGridError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeGridSampleBreakdownError extends InvalidGroupElementResultV1QuestionViewTypeGridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeGridSampleBreakdownError";
  }
}

export class InvalidGroupElementResultV1QuestionViewTypeGridGridResultsError extends InvalidGroupElementResultV1QuestionViewTypeGridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupElementResultV1QuestionViewTypeGridGridResultsError";
  }
}
